import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Eye, FileText } from 'lucide-react';
import { toast } from 'react-hot-toast';
import apiService from '../../services/api';
import DataTable from '../common/DataTable';
import Modal from '../common/Modal';

const News = ({ user }) => {
  const [news, setNews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editingNews, setEditingNews] = useState(null);
  const [selectedNews, setSelectedNews] = useState(null);

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    summary: '',
    author: '',
    category: '',
    status: 'draft',
    featured_image: null
  });

  useEffect(() => {
    fetchNews();
  }, []);

  const fetchNews = async () => {
    try {
      setLoading(true);
      const response = await apiService.getNews();
      setNews(response);
    } catch (error) {
      toast.error('Failed to fetch news');
      console.error('Error fetching news:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const submitData = new FormData();
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== '') {
          submitData.append(key, formData[key]);
        }
      });

      if (editingNews) {
        await apiService.updateNews(editingNews.id, submitData);
        toast.success('News updated successfully');
      } else {
        await apiService.createNews(submitData);
        toast.success('News created successfully');
      }

      setShowModal(false);
      resetForm();
      fetchNews();
    } catch (error) {
      toast.error(error.message || 'Failed to save news');
    }
  };

  const handleView = (newsItem) => {
    setSelectedNews(newsItem);
    setShowViewModal(true);
  };

  const handleEdit = (newsItem) => {
    setEditingNews(newsItem);
    setFormData({
      title: newsItem.title,
      content: newsItem.content,
      summary: newsItem.summary || '',
      author: newsItem.author,
      category: newsItem.category || '',
      status: newsItem.status,
      featured_image: null
    });
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    toast((t) => (
      <div>
        <p>Are you sure you want to delete this news?</p>
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
          <button
            onClick={async () => {
              toast.dismiss(t.id);
              try {
                await apiService.deleteNews(id);
                toast.success('News deleted successfully');
                fetchNews();
              } catch (error) {
                toast.error('Failed to delete news');
              }
            }}
            style={{ background: '#ef4444', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Delete
          </button>
          <button
            onClick={() => toast.dismiss(t.id)}
            style={{ background: '#6b7280', color: 'white', border: 'none', padding: '0.25rem 0.5rem', borderRadius: '4px' }}
          >
            Cancel
          </button>
        </div>
      </div>
    ), { duration: Infinity });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      summary: '',
      author: '',
      category: '',
      status: 'draft',
      featured_image: null
    });
    setEditingNews(null);
  };

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: files ? files[0] : value
    }));
  };



  const columns = [
    {
      key: 'title',
      label: 'Title',
      render: (item) => (
        <div>
          <div className="font-medium">{item.title}</div>
          {item.summary && <div className="text-sm text-gray-500">{item.summary.substring(0, 100)}...</div>}
        </div>
      )
    },
    {
      key: 'author',
      label: 'Author'
    },
    {
      key: 'category',
      label: 'Category',
      render: (item) => item.category || 'Uncategorized'
    },
    {
      key: 'status',
      label: 'Status',
      render: (item) => item.status
    },
    {
      key: 'published_date',
      label: 'Published Date',
      render: (item) => item.published_date ? new Date(item.published_date).toLocaleDateString() : 'Not published'
    },
    {
      key: 'actions',
      label: 'Actions',
      render: (item) => (
        <div className="action-buttons">
          <button onClick={() => handleView(item)} className="btn-sm" title="View">
            <Eye size={16} />
          </button>
          <button onClick={() => handleEdit(item)} className="btn-edit" title="Edit">
            <Edit size={16} />
          </button>
          {user?.role === 'super_admin' && (
            <button onClick={() => handleDelete(item.id)} className="btn-delete" title="Delete">
              <Trash2 size={16} />
            </button>
          )}
        </div>
      )
    }
  ];

  return (
    <div className="news-management">
      <div className="page-header">
        <h2><FileText size={24} /> News Management</h2>
        <motion.button
          className="btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.98 }}
        >
          <Plus size={16} /> Add News
        </motion.button>
      </div>

      <DataTable
        data={news}
        columns={columns}
        loading={loading}
        emptyMessage="No news found"
      />

      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false);
          resetForm();
        }}
        title={`${editingNews ? 'Edit' : 'Add'} News`}
        size="lg"
      >
        <form onSubmit={handleSubmit} style={{ textAlign: 'left' }}>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              name="title"
              required
              value={formData.title}
              onChange={handleInputChange}
            />
          </div>
          <div className="form-group">
            <label>Summary</label>
            <textarea
              name="summary"
              value={formData.summary}
              onChange={handleInputChange}
              rows="3"
            />
          </div>
          <div className="form-group">
            <label>Content</label>
            <textarea
              name="content"
              required
              value={formData.content}
              onChange={handleInputChange}
              rows="6"
            />
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Author</label>
              <input
                type="text"
                name="author"
                required
                value={formData.author}
                onChange={handleInputChange}
              />
            </div>
            <div className="form-group">
              <label>Category</label>
              <input
                type="text"
                name="category"
                value={formData.category}
                onChange={handleInputChange}
              />
            </div>
          </div>
          <div className="form-row">
            <div className="form-group">
              <label>Status</label>
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div className="form-group">
              <label>Featured Image</label>
              <input
                type="file"
                name="featured_image"
                onChange={handleInputChange}
                accept="image/*"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => {
                setShowModal(false);
                resetForm();
              }}
            >
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {editingNews ? 'Update' : 'Create'}
            </button>
          </div>
        </form>
      </Modal>

      {/* View Modal */}
      <Modal
        isOpen={showViewModal}
        onClose={() => setShowViewModal(false)}
        title="News Details"
        size="lg"
      >
        {selectedNews && (
          <div style={{ textAlign: 'left', padding: '0' }}>
            {/* Header Section */}
            <div style={{
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              padding: '2rem',
              borderRadius: '12px 12px 0 0',
              marginBottom: '1.5rem',
              color: 'white',
              textAlign: 'center'
            }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '50%',
                background: 'rgba(255,255,255,0.2)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 1rem',
                fontSize: '2rem',
                fontWeight: 'bold'
              }}>
                <FileText size={32} />
              </div>
              <h3 style={{ margin: '0 0 0.5rem', fontSize: '1.5rem' }}>{selectedNews.title}</h3>
              <p style={{ margin: '0', opacity: '0.9' }}>{selectedNews.category || 'News Article'}</p>
            </div>

            {/* Content Info */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Content</h4>
              <div style={{ display: 'grid', gap: '1rem' }}>
                {selectedNews.summary && (
                  <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                    <strong style={{ color: '#374151' }}>Summary:</strong>
                    <p style={{ margin: '0.5rem 0 0', color: '#6b7280' }}>{selectedNews.summary}</p>
                  </div>
                )}
                <div style={{ padding: '0.75rem', background: '#f9fafb', borderRadius: '8px' }}>
                  <strong style={{ color: '#374151' }}>Content:</strong>
                  <div style={{ margin: '0.5rem 0 0', color: '#6b7280', maxHeight: '200px', overflow: 'auto' }}>
                    {selectedNews.content}
                  </div>
                </div>
              </div>
            </div>

            {/* Meta Information */}
            <div style={{ marginBottom: '1.5rem' }}>
              <h4 style={{ margin: '0 0 1rem', color: '#374151', fontSize: '1.1rem' }}>Details</h4>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Author</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedNews.author || 'N/A'}</div>
                </div>
                <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                  <div style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '0.25rem' }}>Published</div>
                  <div style={{ fontWeight: '500', color: '#374151' }}>{selectedNews.published_date ? new Date(selectedNews.published_date).toLocaleDateString() : 'Not published'}</div>
                </div>
              </div>
            </div>

            {/* Status Badge */}
            <div style={{ textAlign: 'center' }}>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 1rem',
                background: selectedNews.status === 'published' ? '#dcfce7' : selectedNews.status === 'draft' ? '#fef3c7' : '#f3f4f6',
                color: selectedNews.status === 'published' ? '#166534' : selectedNews.status === 'draft' ? '#92400e' : '#374151',
                borderRadius: '20px',
                fontSize: '0.875rem',
                fontWeight: '500'
              }}>
                <div style={{
                  width: '8px',
                  height: '8px',
                  background: selectedNews.status === 'published' ? '#22c55e' : selectedNews.status === 'draft' ? '#f59e0b' : '#6b7280',
                  borderRadius: '50%'
                }} />
                {selectedNews.status}
              </span>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default News;